import React, { useRef, useState } from "react";
import styles from "./index.module.css";
import Progress from "../progress";
import { Button, message } from "antd";
import IconFont from "@meitu/xdesign-icon-font";
import { file2html, turndownService } from "@/utils/markdownHelper";
import { sleep } from "@/utils";
import { statTrack } from "@/utils/mtstat";
import { FILE_TEXT_MAX_LEN } from "@/constants/workbench";
import "antd/es/message/style/css";
import "antd/es/button/style/css";
import useFileUpload from "@/hooks/useFileUpload";
import { uploadFileToObs } from "@/utils/uploadManager";

interface ImportFileProps {
  loading: boolean;
  onClose?: () => void;
  uploadFile?: (file: string, type: string) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  toPasteText?: () => void;
  chancelUpload: React.MutableRefObject<boolean>;
}

const ImportFile = (props: ImportFileProps) => {
  const {
    loading,
    onClose,
    uploadFile,
    setLoading,
    toPasteText,
    chancelUpload,
  } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressRef = useRef<{
    progressFinish: () => void;
  }>();
  const [uploadError, setUploadError] = useState(false);
  const { handleFileChange, handleDrop, handleDragOver, files } = useFileUpload(
    async (uploadedFiles) => {
      console.log("Uploaded files:", uploadedFiles);
      if (uploadedFiles[0]) {
        try {
          const fileNames = uploadedFiles[0].name.split(".");
          console.log("【fileNames】：", fileNames);
          statTrack("tools_click_ppt_home", {
            click_type: "upload_file_begin",
            upload_file_format: fileNames[fileNames.length - 1],
          });
          setLoading(true);
          const file = uploadedFiles[0];
          const nameSplits = file.name.split(".");
          const fileType = nameSplits[nameSplits.length - 1];
          if (
            file.name.endsWith(".pdf") ||
            file.name.endsWith(".ppt") ||
            file.name.endsWith(".pptx")
          ) {
            // pdf 走服务端上传
            const res = await uploadFileToObs(
              file,
              undefined,
              "ai_ppt",
              // '.pdf'
              fileType
            );
            if (chancelUpload.current) {
              return;
            }
            console.log("【上传pdf】：", res);
            const fileUrl = res.url;
            uploadFile?.(fileUrl, fileType);
            setUploadError(false);
            return;
          } else {
            const html = await file2html(file);
            let markdown = turndownService.turndown(html);
            console.log("【markdown格式】：", markdown);
            if (markdown.trim().length === 0) {
              // 内容为空
              message.error("未检测到文字，请确认文件是否全为图片");
              setLoading(false);
              statTrack("tools_click_ppt_home", {
                click_type: "upload_file_fail",
              });
              return;
            }
            if (chancelUpload.current) {
              return;
            }
            if (markdown.length > FILE_TEXT_MAX_LEN) {
              markdown = markdown.substring(0, FILE_TEXT_MAX_LEN);
              message.info("已解析该文件前3w字内容");
            }
            uploadFile?.(markdown, "other");
            setUploadError(false);
            clearFileStatus();
          }
        } catch (error) {
          console.log("上传失败:", error);
          clearFileStatus();
          setUploadError(true);
          statTrack("tools_click_ppt_home", {
            click_type: "upload_file_fail",
          });
        }
      }
    }
  );

  // 重置上传的一些状态
  const clearFileStatus = async () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    progressRef.current?.progressFinish();
    await sleep(1000);
    setLoading(false);
  };

  const fileButtonClick = () => {
    chancelUpload.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    statTrack("tools_click_ppt_home", {
      click_type: "upload_file_touch",
    });
    fileInputRef.current?.click();
  };

  const fileClose = () => {
    chancelUpload.current = true;
    statTrack("tools_click_ppt_home", {
      click_type: "upload_file_fail",
    });
    onClose?.();
    setLoading(false);
    clearFileStatus();
  };

  return (
    <div className={styles.import}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".docx,.pptx,.pdf,.xmind,.txt"
        onChange={handleFileChange}
      />
      {!loading && (
        <div
          className={styles.importContent}
          onDrop={(e) => {
            chancelUpload.current = false;
            handleDrop(e);
          }}
          onDragOver={handleDragOver}
        >
          {!uploadError && (
            <>
              <Button
                className={styles.importButton}
                icon={<IconFont name="UploadFlowBold" fontSize={18} />}
                type="primary"
                onClick={fileButtonClick}
              >
                点击或拖拽文件上传
              </Button>
              <div className={styles.importMessage}>
                *支持上传3万字以内的word、pdf、ppt、xmind、txt等文件格式，不保留图片、图表等内容
              </div>
              <div className={styles.importMessage}>
                *上传的PPT文件会重新润色文本和结构后生成新PPT
              </div>
            </>
          )}
          {uploadError && (
            <>
              <div className={styles.importMessage}>
                <IconFont name="ExclamationMarkCircleBold" fontSize={16} />
                该文件格式暂不支持，可以试试
                <span
                  onClick={() => {
                    toPasteText?.();
                    setTimeout(() => {
                      setUploadError(false);
                    }, 300);
                  }}
                >
                  已有文本生成
                </span>
              </div>

              <Button
                className={styles.afreshButton}
                type="primary"
                onClick={fileButtonClick}
              >
                重新上传
              </Button>
            </>
          )}
        </div>
      )}
      {loading && (
        <div className={styles.importContent}>
          <Progress
            files={files}
            loading={loading}
            onClose={fileClose}
            ref={progressRef}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ImportFile);
